import React, {useEffect, useState} from "react";
import PlotlyPlot from "../../../../common/components/PlotlyPlot";
import {loadProtocolSpikeCountsData} from "../../services/protocols";
import {PlotParams} from "react-plotly.js";
import {IProtocol} from "../../../types/protocol";
import {ICompany} from "@/common/types";
import {Box, Loader, NumberInput, Text, Button} from "@mantine/core";

interface SpikeCountsChartProps {
    activeCompany: ICompany;
    selectedProtocol: IProtocol | undefined;
    refreshTrigger?: number
}

export const SpikeCountsChart: React.FC<SpikeCountsChartProps> = ({activeCompany, selectedProtocol, refreshTrigger}) => {
    const [spikeCountsData, setSpikeCountsData] = useState<PlotParams>()
    const [loading, setLoading] = useState<boolean>(false)
    // thresholdMultiplier reflects the current input value; appliedThreshold is the committed value
    const [thresholdMultiplier, setThresholdMultiplier] = useState<number>(-4.0)
    const [appliedThreshold, setAppliedThreshold] = useState<number>(-4.0)

    useEffect(() => {
        if (selectedProtocol) {
            setLoading(true)
            // Only reload when appliedThreshold changes (user must press Update)
            loadProtocolSpikeCountsData(activeCompany.id, selectedProtocol, appliedThreshold).then((data) => {
                const channelSet = new Set<string>();
                const counts = new Map<string, number>();

                (data.data || []).forEach(([channel, count]: [string, number]) => {
                    channelSet.add(channel);
                    counts.set(channel, count);
                });
                const channelOrder = Array.from(channelSet).sort();

                const barTemplate = (data.figure?.data && (data.figure.data[0] as any)) || {} as any;
                const templateMarker = (barTemplate && barTemplate.marker) || {};
                const baseColor = templateMarker.color ?? templateMarker.fillcolor ?? "rgba(31,119,180,0.8)";

                const countTrace: any = {
                    ...barTemplate,
                    x: channelOrder,
                    y: channelOrder.map((ch) => counts.get(ch) || 0),
                    name: barTemplate.name || "Spike counts",
                    marker: {
                        ...templateMarker,
                        color: baseColor,
                    },
                    type: barTemplate.type || 'bar',
                    hovertemplate: barTemplate.hovertemplate || "%{x}: %{y}<extra></extra>",
                };
                // convert to plotly params format
                // set PlotParams state using server-provided layout
                setSpikeCountsData({
                    layout: {
                        ...data.figure.layout,
                    },
                    config: {responsive: true},
                    // Plotly expects an array of trace objects
                    data: [countTrace],
                });
            }).catch(() => {
                // eat the error since notification would already be called
            }).finally(() => {
                setLoading(false)
            })
        }
    }, [selectedProtocol, refreshTrigger, appliedThreshold])

    const renderLoading = () => {
        return (
            <Box style={{position: "absolute", zIndex: "10", top: "calc(50% - 2em)", left: "calc(50% - 2em)"}}>
                <Loader size="4em" color="var(--bios-strong-accent)"/>
            </Box>
        )
    }

    // Controls + plot layout
    return (
        <Box style={{height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
            <Box style={{flex: '0 0 auto', display: 'flex', gap: '1rem', padding: '0.5rem', alignItems: 'center'}}>
                <Box style={{display: 'flex', flexDirection: 'column', gap: '0.25rem'}}>
                    <Text size="xs" color="dimmed">Threshold (x RMS)</Text>
                    <Box style={{display: 'flex', gap: '0.5rem', alignItems: 'center'}}>
                        <NumberInput
                            value={thresholdMultiplier}
                            onChange={(v) => {
                                if (typeof v === 'number') setThresholdMultiplier(v)
                            }}
                            max={-4}
                            step={0.5}
                            placeholder="Threshold multiplier"
                            style={{width: '7.5rem'}}
                        />
                        <Button size="xs" onClick={() => setAppliedThreshold(thresholdMultiplier)} disabled={appliedThreshold === thresholdMultiplier}>Update</Button>
                    </Box>
                 </Box>
             </Box>

            <Box style={{flex: '1 1 auto', minHeight: 0, position: 'relative', overflow: 'hidden'}}>
                {spikeCountsData ? (
                    <Box style={{height: '100%', width: '100%'}}>
                        <PlotlyPlot {...spikeCountsData}/>
                        {loading ? renderLoading() : null}
                    </Box>
                ) : (
                    <Box style={{height: '100%', position: 'relative'}}>
                        {renderLoading()}
                    </Box>
                )}
            </Box>
        </Box>
    )
}
